import { Component } from '@angular/core';
import { FormControl, FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../../auth/auth.service';
import { UserDto } from '../../../../auth/user.dto';
import { ConfirmationComponent } from '../../../../shared/confirmation/confirmation.component';
import { DisplayLabel } from '../../../../shared/models/displayLabel';
import { InterviewType, labelsByInterviewType } from '../../../../shared/models/enums/interview-type.enum';
import { Presence, labelsByPresence } from '../../../../shared/models/enums/presence.enum';
import { Title } from '../../../../shared/models/enums/title.enum';
import { UserAddressDto } from '../../../../shared/models/user-address.dto';
import { AddressUserService } from '../../../../shared/services/addressUser.service';
import { BeneficiariesService } from '../../../../shared/services/beneficiaries.service';
import { DateService } from '../../../../shared/services/date.service';
import { InterviewThemeService } from '../../../../shared/services/interviewTheme.service';
import { InterviewsService } from '../../../../shared/services/interviews.service';
import { StringUtils } from '../../../../shared/services/stringUtils.service';
import { UserService } from '../../../../shared/services/user.service';
import { Time } from '../../../../shared/time/time';
import { TimeService } from '../../../../shared/time/time.service';
import { ToastService } from '../../../../shared/toast/toast.service';
import { DateValidators } from '../../../../shared/validators/date.validators';
import { BeneficiaryDto } from '../../../dto/beneficiary.dto';
import { Interview } from '../interview';

type InterviewFormType = {
  interviewDate: FormControl<Date | null>;
  beginTimeInterview: FormControl<Time | null>;
  endTimeInterview: FormControl<Time | null>;
  directedBy: FormControl<number | null>;
  interviewType: FormControl<string | null>;
  interviewTheme: FormControl<number | null>;
  presence: FormControl<string | null>;
  address: FormControl<number | null>;
  receptionSiteId: FormControl<number | null>;
  synthesis: FormControl<string | null>;
  note: FormControl<string | null>;
  answerProvidedIds: FormControl<number[] | null>;
};

@Component({
  selector: 'app-interview-details',
  templateUrl: './interview-details.component.html',
})
export class InterviewDetailsComponent {
  public interview: Interview | undefined;
  public interviewDetailsForm: FormGroup<InterviewFormType>;
  public interviewThemes: DisplayLabel[] = [];
  public userList: UserDto[] = [];
  public endTimes: Time[] = [];
  public beginTimes: Time[] = [];
  public answersProvided: DisplayLabel[] = [];
  public isModificationMode: boolean = false;
  public submitted: boolean = false;
  public hasStructure: boolean = true;
  public Title = Title;
  private beneficiaryId: number;
  private interviewId: number;
  private beneficiary: BeneficiaryDto;

  public userAddresses: UserAddressDto[] = [];
  public readonly presences: Map<Presence, string> = labelsByPresence;
  public readonly interviewsTypes: Map<InterviewType, string> = labelsByInterviewType;
  public readonly StringUtils: StringUtils = StringUtils;
  public readonly currentDate: Date;
  public user: UserDto | undefined = undefined;

  private readonly destroy$: Subject<void> = new Subject();

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly modal: NgbActiveModal,
    private readonly interviewService: InterviewsService,
    private readonly interviewThemeService: InterviewThemeService,
    private readonly userService: UserService,
    private readonly timeService: TimeService,
    private readonly authService: AuthService,
    private readonly toastService: ToastService,
    private readonly modalService: NgbModal,
    private readonly beneficiariesService: BeneficiariesService,
    private readonly addressService: AddressUserService,
    private readonly dateService: DateService,
  ) {
    this.currentDate = this.dateService.getCurrent();
  }

  public async onInit(interview?: Interview, beneficiaryId?: number): Promise<void> {
    this.user = await this.authService.getUser();
    this.beneficiaryId = beneficiaryId;
    this.interviewId = interview?.id;
    this.endTimes = this.timeService.initTimes(21);
    this.beginTimes = this.timeService.initTimes(20);
    this.userAddresses = await this.addressService.findByUser(this.user.id);
    if (this.user.groups.includes('RECEPTION')) {
      this.userAddresses = this.userAddresses.filter((addr) => addr.structureId === this.user.structureId);
      this.answersProvided = (await this.interviewService.findAllAnswersProvided())
        .map((value: DisplayLabel) => ({
          category: 'Tous',
          ...value,
        }))
        .sort((a, b) => (a.label < b.label ? -1 : 1));
    }
    this.interviewThemes = await this.interviewThemeService.findAll();
    this.beneficiary = await this.beneficiariesService.findById(beneficiaryId);
    if (this.beneficiary && !this.beneficiary.orientation?.structure.id) {
      this.hasStructure = false;
      this.toastService.show('Le bénéficiaire ne possède pas de structure, impossible de lui créer ni modifier un contact.', 'warning');
    }
    await this.loadForm(interview);
    if (this.interviewId) {
      this.userList = await this.userService.findAll(this.interview.structureId);
    } else if (this.user.groups.includes('STRUCTURE')) {
      this.userList = await this.userService.findAll(this.user.structureId);
    } else if (this.beneficiary?.orientation?.structure.id) {
      this.userList = await this.userService.findAll(this.beneficiary.orientation.structure.id);
    }
    this.interviewDetailsForm.controls.directedBy.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: number) => !this.user.groups.includes('RECEPTION') && this.handleUserChange(value));
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Charger formulaire de creation/consultation contact
   */
  private async loadForm(interview?: Interview): Promise<void> {
    const isReception: boolean = this.user.groups.includes('RECEPTION');
    if (!interview) {
      interview = this.initInterview(isReception);
    } else {
      this.userAddresses = await this.addressService.findByUser(interview.directedById);
    }

    this.interview = interview;
    this.interviewDetailsForm = this.formBuilder.group(
      {
        interviewDate: new FormControl<Date | null>(this.interview.interviewDate, [Validators.required, DateValidators.dateFormatValidator]),
        beginTimeInterview: new FormControl<Time | null>(
          this.timeService.getTime(this.beginTimes, this.interview.beginTimeInterview) ??
            this.beginTimes[this.beginTimes.push(this.interview.beginTimeInterview) - 1],
        ),
        endTimeInterview: new FormControl<Time | null>(
          this.timeService.getTime(this.endTimes, this.interview.endTimeInterview) ??
            this.endTimes[this.endTimes.push(this.interview.endTimeInterview) - 1],
        ),
        directedBy: new FormControl<number | null>(this.interview.directedById, [Validators.required]),
        interviewType: new FormControl<string | null>(this.interview.interviewType, Validators.required),
        interviewTheme: new FormControl<number | null>(this.interview.interviewThemeId, isReception ? Validators.required : undefined),
        presence: new FormControl<string | null>(this.interview.presence),
        address: new FormControl<number | null>(this.interview.userSiteId, !isReception ? Validators.required : Validators.nullValidator),
        receptionSiteId: new FormControl<number | null>(this.interview.receptionSiteId, isReception ? Validators.required : Validators.nullValidator),
        synthesis: new FormControl<string | null>(this.interview.synthesis),
        note: new FormControl<string | null>(this.interview.note),
        answerProvidedIds: new FormControl<number[] | null>(this.interview.answerProvidedIds, isReception ? Validators.required : undefined),
      },
      {
        validators: [DateValidators.timesOrderValidator('beginTimeInterview', 'endTimeInterview')],
      },
    );
    this.isModificationMode = true;
  }

  private initInterview(isReception: boolean): Interview {
    const currentTime: Time = new Time(this.currentDate.getHours(), this.currentDate.getMinutes());
    if (isReception) {
      this.beginTimes.push(currentTime);
      this.endTimes.push(currentTime);
    }

    return {
      interviewDate: isReception ? this.currentDate : undefined,
      beginTimeInterview: isReception ? currentTime : new Time(9, 0),
      endTimeInterview: isReception ? currentTime : new Time(10, 0),
      directedById: (this.user.groups.includes('STRUCTURE') || this.user.groups.includes('RECEPTION')) && this.user.id ? this.user.id : undefined,
      interviewType: isReception ? Object.keys(InterviewType)[Object.values(InterviewType).indexOf(InterviewType.Reception)] : undefined,
      presence: isReception ? Object.keys(Presence)[Object.values(Presence).indexOf(Presence.Presence)] : undefined,
      receptionSiteId: isReception ? this.userAddresses[0]?.siteAddressId : null,
    } as Interview;
  }

  public async onSubmit(): Promise<void> {
    this.submitted = true;
    if (!this.interviewDetailsForm.valid) {
      this.toastService.show('La sauvegarde a échoué, veuillez vérifier les données.', 'danger');
      return;
    }

    this.interview = new Interview(
      this.interviewId,
      this.interviewDetailsForm.controls.interviewDate.value,
      this.interviewDetailsForm.controls.beginTimeInterview.value,
      this.interviewDetailsForm.controls.endTimeInterview.value,
      this.interviewDetailsForm.controls.directedBy.value,
      this.interviewDetailsForm.controls.interviewType.value,
      this.beneficiaryId,
      this.interviewDetailsForm.controls.interviewTheme.value,
      this.interviewDetailsForm.controls.presence.value,
      this.interviewDetailsForm.controls.address.value,
      this.interviewDetailsForm.controls.receptionSiteId.value,
      this.interviewDetailsForm.controls.synthesis.value,
      this.interviewDetailsForm.controls.note.value,
      this.interviewDetailsForm.controls.answerProvidedIds.value,
    );

    try {
      if (this.interview) {
        await this.interviewService.saveInterview(this.interview);
        this.toastService.show('Le contact a été sauvegardé avec succès.', 'success');
        this.modal.close();
      }
    } catch (error) {
      if (error.status === 460) {
        this.toastService.show('Aucun accompagnement valide à la date du contact.', 'danger');
      } else if (error.status === 461) {
        if (this.user?.groups.includes('STRUCTURE')) {
          this.toastService.show('Aucune orientation valide dans votre structure à la date du contact.', 'danger');
        } else {
          this.toastService.show('Aucune orientation valide à la date du contact.', 'danger');
        }
      } else if (error.status === 473) {
        this.toastService.show('Un événement existe déjà sur le créneau sélectionné.', 'danger');
      }
    }
  }

  public cancel(): void {
    this.interviewDetailsForm.reset();
    this.modal.close();
  }

  public setEndTimeValue(): void {
    if (this.interviewDetailsForm) {
      const beginTime: Time = this.interviewDetailsForm.controls.beginTimeInterview.value;
      this.interviewDetailsForm.controls.endTimeInterview.setValue(
        this.endTimes.filter((time: Time) => time.hour === beginTime.hour + 1 && time.minute === beginTime.minute)[0],
      );
    }
  }

  public async downloadDocument(type: string): Promise<void> {
    await this.interviewService.downloadDocument(this.interview.id, type);
  }

  public async deleteInterview(): Promise<void> {
    const confirm: boolean = await this.openConfirmationModal();
    if (confirm) {
      await this.interviewService.deleteInterview(this.interview.id);
      this.toastService.show('Le contact a été supprimé avec succès.', 'success');
      this.modal.close();
    }
  }

  private async openConfirmationModal(): Promise<boolean> {
    const modal: NgbModalRef = this.modalService.open(ConfirmationComponent, {
      backdrop: 'static',
      centered: true,
    });
    const modalComponent: ConfirmationComponent = modal.componentInstance as ConfirmationComponent;
    return await modalComponent.confirm('Êtes-vous sûr(e) de vouloir supprimer cet entretien ?');
  }

  private async handleUserChange(userId: number): Promise<void> {
    if (userId) {
      this.interviewDetailsForm.controls.address.reset();
      this.userAddresses = await this.addressService.findByUser(userId);
    }
  }
}
